import gsap from "gsap";
import SplitText from "gsap/SplitText";
import classNames from "classnames/bind";
import { useEffect, useMemo, useRef, useState } from "react";
import { GetAllInducteesQuery } from "@/__generated__/graphql";
import { getLinkData, sortInductees } from "@/core/utils";
import ExpandIcon from "@/assets/svg/expand.svg";
import CollapseIcon from "@/assets/svg/collapse.svg";

import { useViewSize } from "@/providers/ViewSizeContext";

import styles from "../InducteesByClass.module.scss";
import { Button } from "@/components";
import { useClientMount } from "@/providers/ClientMountProvider";
const cx = classNames.bind(styles);

type Inductee = GetAllInducteesQuery["allInductees"][number];

type InducteesByClassProps = {
  year: string;
  inductees: Inductee[];
  activeYear: string | null;
  primaryCardCta?: CTAData;
  onYearClick: (year: string) => void;
  onTLComplete?: () => void;
};

const InducteeListItem = ({ inductee }: { inductee: Inductee }) => {
  const { mounted } = useClientMount();
  const { isReady } = useViewSize();
  const nameEl = useRef(null);
  
  useEffect(() => {
    if (!mounted || !isReady || !nameEl.current) return;
    
    gsap.registerPlugin(SplitText);
    setTimeout(() => {
      SplitText.create(nameEl.current, { type: "lines" });
    }, 150);
  }, [mounted, isReady]);
  return (
    <li key={inductee.title} className={cx("inductee-item")}>
      <p ref={nameEl} className={cx(["name"])}>
        {inductee.title}
      </p>
      <span className={cx("spacer")} />
    </li>
  );
};

export default function InducteesByClassList({
  year,
  inductees,
  onYearClick,
  activeYear,
  primaryCardCta,
  onTLComplete,
}: InducteesByClassProps) {
  const rootEl = useRef<HTMLButtonElement>(null);
  const expandedContentEl = useRef<HTMLDivElement>(null);
  const collapsedYearHeaderEl = useRef<HTMLParagraphElement>(null);
  const inducteesListEl = useRef<HTMLUListElement>(null);
  const originalHeight = useRef<number>(0);
  const { isMobile, isTablet, isDesktop, viewHeight, isReady } = useViewSize();
  const { mounted } = useClientMount();
  const [isFirst, setIsFirst] = useState(true);
  let tl: gsap.core.Timeline;

  let [maxItems, setMaxItems] = useState(7);
  const sortedInductees = useMemo(
    () => sortInductees(inductees).slice(0, maxItems),
    [inductees, maxItems]
  );
  const ctaData = useMemo(() => {
    return getLinkData(primaryCardCta);
  }, [primaryCardCta]);

  const handleButtonClick = () => {
    onYearClick(year);
  };

  const handleInnerClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  useEffect(() => {
    if (!mounted || !isReady) return;
    if (activeYear) {
      let cardWidth = "29rem";
      let cardHeight = "6rem";

      if (!isMobile) {
        cardWidth = "32rem";
        cardHeight = "8rem";
      }

      if (originalHeight.current === 0) {
        originalHeight.current = (
          rootEl.current.parentNode as HTMLDivElement
        ).offsetHeight;
      }

      gsap.killTweensOf(rootEl.current);
      gsap.killTweensOf(rootEl.current.parentNode);
      gsap.to(rootEl.current, {
        width: cardWidth,
        height: cardHeight,
        duration: 0.4,
        x: 0,
        overflow: "hidden",
        ease: "power2.out",
      });
      gsap.to(rootEl.current.parentNode, {
        width: cardWidth,
        height: cardHeight,
        duration: 0.4,
        ease: "power2.out",
      });
      gsap.to(expandedContentEl.current, {
        opacity: 0,
        duration: 0.35,
        ease: "power2.out",
      });
      gsap.to(collapsedYearHeaderEl.current, {
        autoAlpha: 1,
        duration: 0.35,
        delay: 0.25,
        ease: "power2.out",
      });
    } else if (!activeYear && !isFirst) {
      gsap.killTweensOf(rootEl.current);
      gsap.killTweensOf(rootEl.current.parentNode);

      gsap.to(rootEl.current.parentNode, {
        height: originalHeight.current !== 0 ? originalHeight.current : "auto",
        width: "revert-layer",
        duration: 0.4,
        ease: "power2.out",
      });
      gsap.to(rootEl.current, {
        width: "100%",
        overflow: "hidden",
        duration: 0.4,
        ease: "power2.out",
      });
      gsap.to(rootEl.current, {
        height: "auto",
        duration: 0.4,
        ease: "power2.out",
        delay: 0.1,
      });
      gsap.to(collapsedYearHeaderEl.current, {
        autoAlpha: 0,
        duration: 0.35,
        ease: "power2.out",
      });
      gsap.to(expandedContentEl.current, {
        opacity: 1,
        duration: 0.35,
        delay: 0.25,
        ease: "power2.out",
      });
    }

    // isFirst.current = false;
    setIsFirst(false);
  }, [mounted, isReady, activeYear, isFirst, rootEl, isMobile, isDesktop, isTablet]);

  useEffect(() => {
    let maxItems = 7
    if (viewHeight > 1100) maxItems = 8;
    if (viewHeight > 1200 || isDesktop) maxItems = 14;
    setMaxItems(maxItems);
  }, [viewHeight])

  return (
    <button
      className={cx("inductees-by-class-list", { active: activeYear === year })}
      onClick={handleButtonClick}
      ref={rootEl}
      aria-selected={activeYear === year}
    >
      <div className={cx("expand-icon")}>
        {!!activeYear ? (
          activeYear === year ? (
            <CollapseIcon />
          ) : (
            <ExpandIcon />
          )
        ) : (
          <ExpandIcon />
        )}
      </div>

      <div className={cx("collapsed-year-header")} ref={collapsedYearHeaderEl}>
        <p className="title-lc-xs">
          class of <span className={cx("highlighted-text")}>{year}</span>
        </p>
      </div>
      <div aria-hidden={!!activeYear} className={cx("expanded-content")} ref={expandedContentEl}>
        <div className={cx("year-header")}>
          <p className={cx(["class", "text-l"])}>class of</p>
          <p className={cx(["year", "display-xxl"])}>{year}</p>
        </div>
        <div className={cx("expanded-content__content")}>
          <ul className={cx("inductees-list")} ref={inducteesListEl}>
            {sortedInductees.map((inductee, index) => (
              <InducteeListItem
                key={`${inductee.title}-${index}`}
                inductee={inductee}
              />
            ))}
            {inductees.length > maxItems && (
              <li className={cx("inductee-item")}>
                <p className={cx(["name"])}>+ more</p>
              </li>
            )}
          </ul>
          {ctaData && <Button {...ctaData} onClick={handleInnerClick} />}
        </div>
      </div>
    </button>
  );
}
